import React, { memo, useContext, useRef } from 'react';
import { Col, Modal, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { SearchGroup } from '@components/input';
import { IFilterSelectItems } from '@redux/slices/order';
import { ExportButton } from '@styles/components/button';
import { ModalContext } from 'src/contexts/modal-context';
import { campaignSelector, exportCampaign, filterCampaign, importCotas, importCotasLiga } from '@redux/slices/campaign';
import { EditCampaignModal } from './edit-modal';
import { translate } from '@components/i18n';
import { ExclamationCircleOutlined, ImportOutlined } from '@ant-design/icons';
import { authSelector } from '@redux/slices/auth';
import { addNotification } from '@redux/slices/notification';
import { IS_BR } from '@config/env';

const { confirm } = Modal;
export const Header: React.FC = memo(() => {
  const dispatch = useDispatch();
  const authState = useSelector(authSelector);
  const modalContext = useContext(ModalContext);
  const { isExporting, isImporting } = useSelector(campaignSelector);
  const refInput = useRef<HTMLInputElement>(null);

  const filter = (filter: Partial<IFilterSelectItems>): void => {
    dispatch(filterCampaign(filter.filterValue));
  };

  const addUser = (): void => {
    modalContext.setIsOpen(true, <EditCampaignModal />, {
      title: translate('general.addNewCampain'),
      width: '70%',
    });
  };

  const onExport = (): void => {
    dispatch(exportCampaign());
  };

  const openInput = (): void => {
    if (!refInput.current) return;
    refInput.current.click();
  };

  const importItems = (files: FileList) => {
    const file = files[0];
    const formData = new FormData();
    formData.append('file', file);

    confirm({
      title: translate('general.importQuotasConfirmTitle'),
      content: translate('general.importQuotasConfirmMessage'),
      icon: <ExclamationCircleOutlined />,
      className: 'confirm-modal-styled',
      okText: translate('general.yes'),
      cancelText: translate('general.no'),
      okButtonProps: { type: 'primary', danger: true },
      cancelButtonProps: { type: 'primary', className: 'dark-blue-btn' },
      onOk() {
        dispatch(importCotas({ formData, fileName: file.name }));
      },
      onCancel() {
        if (refInput.current) {
          refInput.current.value = '';
        }
      },
    });

    if (refInput.current) {
      refInput.current.value = '';
    }
  };

  const onImportBR = (): void => {
    const divisao = authState.currentUser?.division;
    if (!divisao) {
      dispatch(
        addNotification({
          type: 'error',
          message: translate('general.importQuotasNoDivision'),
          title: translate('general.errorT'),
        }),
      );
      return;
    }

    confirm({
      title: translate('general.importQuotasLigaConfirmTitle'),
      content: `${translate('general.importQuotasLigaConfirmMessage')} (${divisao})`,
      icon: <ExclamationCircleOutlined />,
      className: 'confirm-modal-styled',
      okText: translate('general.yes'),
      cancelText: translate('general.no'),
      okButtonProps: { type: 'primary', danger: true },
      cancelButtonProps: { type: 'primary', className: 'dark-blue-btn' },
      onOk() {
        dispatch(importCotasLiga({ divisao }));
      },
    });
  };

  const onImportClick = (): void => {
    if (IS_BR) onImportBR();
    else openInput();
  };

  return (
    <Col>
      <Row style={{ marginTop: 20 }} align="middle">
        <Col span={24}>
          <Row justify={'space-between'} align="middle">
            <Col span={10}>
              <SearchGroup
                onBlur={(e) =>
                  filter({ filterValue: e.target.value })
                }
                allowClear
                placeholder={translate(
                  'forms.placeholders.searchByName',
                )}
                onSearch={(e) => {
                  filter({ filterValue: e });
                }}
                onChange={(e) => {
                  filter({ filterValue: e.target.value });
                }}
              />
            </Col>
            <Col span={14}>
              <Row justify={'end'}>
                <Col style={{ marginRight: 10, display: 'flex', gap: 4 }}>
                  <input
                    ref={refInput}
                    type='file'
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: 'none' }}
                    onChange={(e) => importItems(e.target.files)}
                  />
                  <ExportButton onClick={addUser}>
                    {translate('forms.buttons.addNewF')}
                  </ExportButton>
                  {authState.isAdmin && (
                    <ExportButton onClick={onImportClick} loading={isImporting} disabled={isImporting || isExporting}>
                      {translate('general.importCotas')} <ImportOutlined />
                    </ExportButton>
                  )}
                  <ExportButton onClick={onExport}>
                    {translate('general.export')}
                  </ExportButton>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Col>
  );
});
